import React from "react";
import defaults from "lodash/defaults";
import get from "lodash/get";
import find from "lodash/find";
import moment from "moment";

import AddToCalendarModal from "../../../../parts/modals/add-to-calendar";
import SubmitForReviewModal from "../../../../parts/modals/submit-for-review";
import EditProgramSettingsModal from "../../../../parts/modals/edit-program-settings";
import EditCycleSettingsModal from "../../../../parts/modals/edit-cycle";
import ProgramSettingsModal from "../../../../parts/modals/program-settings";
import SuggestUpgradeModal from "../../../../parts/modals/suggest-upgrade";

import View from "./ProgramOverview";
import ConfirmPushModal from "../components/ConfirmPush";
import SendModal from "../../../coaching-portal/common-components/SendModal";

import FetchModal from "../../../coaching-portal/common-components/FetchModal";
import { loadCopiableCycles } from "../../../../utils/copiable-cycle";
import { loaderCacheKey } from "../../../../utils/rill-loader-cache";
import ActivityDrawer from "../../../../parts/activity-drawer/ActivityDrawer";

const ProgramOverviewController = async ({
  res,
  req,
  locals,
  load,
  api,
  session,
}) => {
  const { query, params } = req;

  defaults(query, {
    program: "1",
    units: locals.units,
    period: "week",
    type: "total",
  });

  const modal = get(query, "modal", {});
  if (modal.type === "add-to-calendar") {
    await load("activeExercises", {
      start: moment(-1),
      end: moment("December 30 2049"),
    });
  }
  const proxyAthlete = get(locals, "athlete._id", "");
  // This route's id is params.cycleId. query.cycle is only present after a
  // modal refresh, so the old load fetched every exercise for the athlete.
  const cycleId = query.cycle || params.cycleId;
  const exerciseArgs = { cycle: cycleId, proxyAthlete };
  const exerciseLoad = cycleId
    ? load("editExercises", exerciseArgs).then((exercises) => {
        // Safe to drop: nothing on this page reads the list back from the
        // session, and a large athlete's exercises don't fit beside the
        // other cached loaders. Same object the loader hashed, so the key matches.
        session.delete(loaderCacheKey("editExercises", exerciseArgs));
        return exercises;
      })
    : Promise.resolve().then(() => {
        locals.editExercises = [];
      });
  await Promise.all([
    exerciseLoad,
    load("editCycle", { cycle: params.cycleId, proxyAthlete }),
    load("libraryCycles"),
    // Whether this client's calendar is already governed by a team or
    // flexible program - a push has to be blocked while that's true, since
    // it writes straight onto the same calendar.
    load("athleteTeam", { proxyAthlete }),
    load("athleteFlexibleProgram", { proxyAthlete }),
    load("exercisesVolumeForCycle", {
      id: params.cycleId,
      program: query.program,
      proxyAthlete,
    }),
    load("exercisesLiftsForCycle", {
      id: params.cycleId,
      program: query.program,
      proxyAthlete,
    }),
    load("exercisesVolumeGroupZonesForCycle", {
      id: params.cycleId,
      program: query.program,
      proxyAthlete,
    }),
  ]);

  locals.activeProgramIndex = req.query.program - 1 || 0;
  // if (locals.activeProgramIndex > locals.editCycle.programs.length - 1 || locals.ActiveProgramIndex < 0) locals.activeProgramIndex = 0;
  const cycle = locals.editCycle
    ? locals.editCycle
    : find(locals.libraryCycles, { _id: locals.params.cycleId });

  const programPosition = query.program ? query.program - 1 : 0;
  const program = cycle.programs[programPosition];

  switch (modal.type) {
    case "fetch":
      await load("athleteCycles", { proxyAthlete });
      await loadCopiableCycles(
        { api, locals },
        query.fetchSourceOwner || locals.activeUserToRequest._id
      );
      break;
    case "send":
      await load("athleteCycles", { proxyAthlete });
      await load("allAthletesFromOrganization");
      await load("clientsOnProgram");
      if (query.recipient)
        await load("unPushedAthleteCyclesNames", query.recipient);
      break;
    default:
      break;
  }

  locals.heading = ["Coaching", "Programs", cycle.name];

  const clientOnProgram = locals.athleteTeam?.length
    ? "team"
    : locals.athleteFlexibleProgram?.length
      ? "flexible"
      : null;

  locals.modal = {
    "add-to-calendar": <AddToCalendarModal cycle={cycle} />,
    "edit-program-settings": <EditProgramSettingsModal />,
    "submit-for-review": <SubmitForReviewModal />,
    "edit-cycle-settings": <EditCycleSettingsModal />,
    "program-settings": <ProgramSettingsModal />,
    "suggest-upgrade": <SuggestUpgradeModal api={api} />,
    "confirm-push": (
      <ConfirmPushModal
        fullProgram={program}
        athlete={locals.athlete}
        clientOnProgram={clientOnProgram}
      />
    ),
    fetch: <FetchModal />,
    send: <SendModal />,
    "activity-feed": <ActivityDrawer />,
  }[get(locals.query, "modal.type")];

  res.body = <View />;
};

export default ProgramOverviewController;
